/* SearchControlComponent.js */

"use strict";

const EventEmitter = require("events").EventEmitter;
const SearchControlRenderer = require("./SearchControlRenderer");
const _ = require("lodash");

class SearchControlComponent extends EventEmitter {
    constructor(config, editor, editorSession) {
        super();
        this._editor = editor;
        this._editorSession = editorSession;
        this._searchString = _.get(config, "searchString", "");
        this._searchCaseSensitive = config.searchCaseSensitive === true;
        this._scrollLock = config.scrollLock === true;
        this._buildActionBar = config.buildActionBar;

        this._renderer = new SearchControlRenderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}

    _unregisterListeners() {}

    _triggerSearchStringChangedEvent() {
        this.emit("UserSearchStringChanged", this.searchString);
    }

    _triggerSearchCaseSensitiveChangedEvent() {
        this.emit("UserSearchCaseSensitiveChanged", this.searchCaseSensitive);
    }

    _triggerSearchResultCountChanged(newSearchResultsCount, oldSearchResultsCount) {
        if (!this._buildActionBar) {
            this.emit(
                "UserSearchResultsCountChanged",
                newSearchResultsCount,
                oldSearchResultsCount
            );
        }
    }

    _triggerCurrentSearchStringSequenceNumberChanged(
        newCurrentSearchStringSequenceNumberChanged,
        oldCurrentSearchStringSequenceNumberChanged
    ) {
        if (!this._buildActionBar) {
            this.emit(
                "CurrentSearchStringSequenceNumberChanged",
                newCurrentSearchStringSequenceNumberChanged,
                oldCurrentSearchStringSequenceNumberChanged
            );
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        if (!this.renderer) {
            return Promise.resolve();
        } else {
            return this._renderer.shutdown().then(
                () => {
                    this._editor = undefined;
                    this._editorSession = undefined;
                    this._searchString = undefined;
                    this._searchCaseSensitive = undefined;
                    this._renderer = undefined;
                },
                () => {
                    //Error handling on renderer shut down
                }
            );
        }
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /*******************************************/
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * @schema.methods.setSearchString.name setSearchString
     * @schema.methods.setSearchString.description Set string to search logs on UI
     * @schema.methods.setSearchString.returns Instance of the control
     * @schema.methods.setSearchString.parameters.newSearchString.name newSearchString
     * @schema.methods.setSearchString.parameters.newSearchString.description new search string
     * @schema.methods.setSearchString.parameters.newSearchString.type String
     */
    setSearchString(newSearchString, userTriggered = false) {
        if (this._searchString !== newSearchString) {
            this._searchString = newSearchString ? newSearchString : "";
            this.emit("searchString", newSearchString);
            if (userTriggered) {
                this._triggerSearchStringChangedEvent();
            }
        }
        return this;
    }

    /**
     * @schema.methods.getSearchString.name getSearchString
     * @schema.methods.getSearchString.description Returns log search string
     * @schema.methods.getSearchString.returns String
     */
    getSearchString() {
        return this._searchString;
    }

    set searchString(newSearchString) {
        return this.setSearchString(newSearchString);
    }

    get searchString() {
        return this.getSearchString();
    }

    /**
     * @schema.methods.setSearchCaseSensitive.name setSearchCaseSensitive
     * @schema.methods.setSearchCaseSensitive.description Decides if the search is case sensitive
     *                                              if set to true - search is case sensitive
     *                                              else - search is NOT case sensitive
     * @schema.methods.setSearchCaseSensitive.returns Instance of the control
     * @schema.methods.setSearchCaseSensitive.parameters.isSearchCaseSensitive.name isSearchCaseSensitive
     * @schema.methods.setSearchCaseSensitive.parameters.isSearchCaseSensitive.description new search case sensitive property
     * @schema.methods.setSearchCaseSensitive.parameters.isSearchCaseSensitive.type Boolean
     */
    setSearchCaseSensitive(isSearchCaseSensitive, userTriggered = false) {
        if (
            isSearchCaseSensitive !== this._searchCaseSensitive &&
            _.isBoolean(isSearchCaseSensitive)
        ) {
            this._searchCaseSensitive = isSearchCaseSensitive;
            this.emit("searchCaseSensitive", this._searchCaseSensitive);
            if (userTriggered) {
                this._triggerSearchCaseSensitiveChangedEvent();
            }
        }
        return this;
    }

    /**
     * @schema.methods.isSearchCaseSensitive.name isSearchCaseSensitive
     * @schema.methods.isSearchCaseSensitive.description Returns if the search is case sensitive
     * @schema.methods.isSearchCaseSensitive.returns Boolean
     */
    isSearchCaseSensitive() {
        return this._searchCaseSensitive;
    }

    set searchCaseSensitive(isSearchCaseSensitive) {
        return this.setSearchCaseSensitive(isSearchCaseSensitive);
    }

    get searchCaseSensitive() {
        return this.isSearchCaseSensitive();
    }

    /**
     * @schema.methods.setScrollLock.name setScrollLock
     * @schema.methods.setScrollLock.description if true do not scroll to end of text on append logs
     * @schema.methods.setScrollLock.returns Instance of this control
     * @schema.methods.setScrollLock.parameters.scrollLock.name scrollLock
     * @schema.methods.setScrollLock.parameters.scrollLock.description if true do not scroll to end of text on append logs
     * @schema.methods.setScrollLock.parameters.scrollLock.type {Boolean}
     */
    setScrollLock(scrollLock) {
        if (this._scrollLock !== scrollLock) {
            this._scrollLock = scrollLock === true;
            this.emit("scrollLock", scrollLock);
        }
        return this;
    }

    /**
     * @schema.methods.getScrollLock.name getScrollLock
     * @schema.methods.getScrollLock.description Returns if editor scrolls to bottom on appending logs
     * @schema.methods.getScrollLock.returns Boolean
     */
    getScrollLock() {
        return this._scrollLock;
    }

    set scrollLock(scrollLock) {
        return this.setScrollLock(scrollLock);
    }

    get scrollLock() {
        return this.getScrollLock();
    }

    /**
     * @schema.methods.scrollToBottom.name scrollToBottom
     * @schema.methods.scrollToBottom.description Triggers an event to parent component to scroll to bottom
     */
    scrollToBottom() {
        this.emit("scrollToBottom");
    }

    /**
     * @schema.methods.getBuildActionBar.name getBuildActionBar
     * @schema.methods.getBuildActionBar.description Returns if action bar is built on UI
     * @schema.methods.getBuildActionBar.returns Boolean
     */
    getBuildActionBar() {
        return this._buildActionBar;
    }

    get buildActionBar() {
        return this.getBuildActionBar();
    }

    highlightNextSearchStringMatch() {
        this.emit("highlightNextSearchStringMatch");
    }

    highlightPreviousSearchStringMatch() {
        this.emit("highlightPreviousSearchStringMatch");
    }
    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = SearchControlComponent;
